/**
 * @file `LearnersTable` — operator-tool table of learners.
 * @module @labcraft/admin/ui/learners/learners-table
 *
 * Read-only view. Click anywhere in a row to open the detail drawer
 * via `onSelect(id)`. The email cell is the one exception: it's a
 * deep-link into the Emails sub-app pre-scoped to that recipient
 * (#1598 Part 10), and it `stopPropagation`s so it doesn't also open
 * the drawer. Empty / loading states render inline rows so the table
 * chrome stays visible.
 */

import type { JSX } from 'react'
import { Link } from 'react-router'
import type {
  Learner,
  LearnerSortField,
  LearnerSortDir,
} from '../../shared/routes.ts'
import { StatusPill } from './status_pill.tsx'

/** Props for {@link LearnersTable}. */
export type LearnersTableProps = {
  learners: Learner[] | null
  loading: boolean
  selectedId: string | null
  onSelect: (id: string) => void
  sort: LearnerSortField | null
  dir: LearnerSortDir
  onSort: (field: LearnerSortField) => void
}

/**
 * Column definitions. `field` marks a column sortable (clicking its
 * header sorts by that column); `Email` and `Status` have no server sort
 * and stay plain labels.
 */
const COLUMNS: ReadonlyArray<{ label: string; field?: LearnerSortField }> = [
  { label: 'Email' },
  { label: 'Name', field: 'displayName' },
  { label: 'Status' },
  { label: 'Signed up', field: 'createdAt' },
  { label: 'Last login', field: 'lastLogin' },
]

/** Render the learners table. */
export function LearnersTable({
  learners,
  loading,
  selectedId,
  onSelect,
  sort,
  dir,
  onSort,
}: LearnersTableProps): JSX.Element {
  return (
    <table className="table">
      <thead>
        <tr>
          {COLUMNS.map(c =>
            c.field ? (
              <th key={c.label}>
                <button
                  type="button"
                  className="th-sort"
                  onClick={() => {
                    onSort(c.field!)
                  }}>
                  {c.label}
                  {sort === c.field && (
                    <span className="th-sort__arrow" aria-hidden="true">
                      {dir === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </button>
              </th>
            ) : (
              <th key={c.label}>{c.label}</th>
            ),
          )}
        </tr>
      </thead>
      <tbody>
        {loading && learners === null && (
          <tr>
            <td className="empty-row" colSpan={COLUMNS.length}>
              // loading…
            </td>
          </tr>
        )}
        {!loading && learners && learners.length === 0 && (
          <tr>
            <td className="empty-row" colSpan={COLUMNS.length}>
              // no learners match this filter.
            </td>
          </tr>
        )}
        {learners?.map(l => (
          <tr
            key={l.id}
            aria-selected={l.id === selectedId}
            onClick={() => {
              onSelect(l.id)
            }}>
            <td className="cell-email">
              <Link
                to={`/emails?recipient=${l.id}`}
                className="cell-email__link"
                title="Compose an email to this learner"
                onClick={e => {
                  // Don't let the deep-link also open the row's detail drawer.
                  e.stopPropagation()
                }}>
                {l.email}
              </Link>
            </td>
            <td
              className={
                l.displayName ? 'cell-name' : 'cell-name cell-name--empty'
              }>
              {l.displayName || '—'}
            </td>
            <td>
              <StatusPill status={l.status} />
            </td>
            <td className="cell-meta">{formatShortDate(l.createdAt)}</td>
            <td className="cell-meta">{formatShortDate(l.lastLoginAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** `2026-05-21T12:00:00Z` → `May 21`. */
function formatShortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
